import CancelChangesModal from '@/components/common/CancelChangesModal/cancelChangesModal';
import { IAlbum } from '@/data/models/album';
import { IValidationResults } from '@/data/models/validation';
import { useProductEditMode } from '@/hooks/useProductEditMode';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'react-tabs/style/react-tabs.css';
import ValidationErrors from '../ProductView/ValidationErrors/ValidationErrors';
import { NestedModalContext } from '@/context/NestedModalContext';
import {
  AccountExclusiveReleaseFormGroup,
  ArtistFormGroup,
  BoxCountFormGroup,
  CatalogNumberFormGroup,
  CopyrightLineFormGroup,
  CountryOfManufactureFormGroup,
  ExplicitLyricsFormGroup,
  GenreFormGroup,
  GtinFormGroup,
  IngroovesCanadaRightsFormGroup,
  InternalDescriptionFormGroup,
  LabelFormGroup,
  ManufacturedByVmgFormGroup,
  ManufacturingCostFormGroup,
  MarketingInformationFormGroup,
  MetadataLanguageFormGroup,
  OriginalReleaseDateFormGroup,
  PackagingDescriptionFormGroup,
  PackagingFormGroup,
  PhonographicCopyrightLineFormGroup,
  ProductVersionsFormGroup,
  ProjectedPhysicalIOFormGroup,
  ProjectOverviewFormGroup,
  ReleaseDateFormGroup,
  ReleaseTypeFormGroup,
  RetailPreOrderDateFormGroup,
  TitleFormGroup,
  UnitsPerSetFormGroup,
  VideoContentTypeFormGroup,
} from './FormGroup';
import './ProductDetailsEdit.scss';
import useFindErrorMessage from './useFindErrorMessage';
import { FormGroupProps } from './FormGroup/types';
import { IChangeRequest } from '@/data/models/changeRequest';
import ChangeRequestIndicator from '@/components/common/ChangeRequestIndicator/ChangeRequestIndicator';
import { ICurrentProductMetadataChangesGroup } from '@/types/product-current-changes-types';
import DismissibleErrorAlert from '@/components/common/ErrorAlert/DismissibleErrorAlert';

interface Props {
  togglePopUp: any;
  albumObj: IAlbum;
  errorFlag: boolean;
  errors: IValidationResults[];
  readonly?: boolean;
  disabled?: boolean;
  showValidationErrors: boolean;
  onProductUpdated: () => void;
  changeRequest?: IChangeRequest;
  currentChanges?: ICurrentProductMetadataChangesGroup;
}

const ProductDetailsEdit = ({
  togglePopUp,
  albumObj,
  errorFlag,
  errors,
  readonly,
  disabled,
  showValidationErrors,
  onProductUpdated,
  changeRequest,
  currentChanges,
}: Props) => {
  const { t } = useTranslation();
  const {
    album,
    setAlbum,
    packagings,
    metadataLanguages,
    territories,
    genres,
    albumValidationMetadata,
    errorMessages,
    errorBanner,
    saveErrorMessage,
    setSaveErrorMessage,
    showPopUp,
    showCancelChangesModal,
    setShowCancelChangesModal,
    isSaveDisabled,
    isSubmitting,
    albumErrors,
    albumIssues,
    showErrors,
    originalReleaseDateOpen,
    setOriginalReleaseDateOpen,
    useReleaseDateVisible,
    activateUseReleaseDate,
    setActivateUseReleaseDate,
    onChangePreorderDate,
    setOnChangePreorderDate,
    onClickTargetAccount,
    setOnClickTargetAccount,
    onChangeTargetAccount,
    setOnChangeTargetAccount,
    releaseTypes,
    yesOption,
    noOption,
    loadLabels,
    loadGenres,
    loadReleaseTypes,
    loadPackaging,
    loadTerritories,
    loadMetadataLanguages,
    isVideoProduct,
    handleManufacturing,
    handleManufacturingCostBlur,
    handleRadioButton,
    handleAlbumMetaData,
    handleSaleProjection,
    handleAlbumField,
    handleSubmit,
    handleGenre,
    handleVersion,
    handleArtist,
    handleAccountExclusiveChange,
    handleLabelSelect,
    handleMarketingInfo,
    onCancel,
    closePopUp,
    handleReleaseType,
    handleVideoContentType,
    handlePackaging,
    useReleaseDate,
    onOriginalReleaseDateChange,
    isOriginalReleaseDateInvalid,
    getCurrentReleaseDate,
    getPreviousReleaseDate,
    fieldHasErrors,
    fieldValidationState,
  } = useProductEditMode({
    togglePopUp,
    albumObj,
    errorFlag,
    errors,
    showValidationErrors,
    onProductUpdated,
    changeRequest,
  });

  const findErrorMessage = useFindErrorMessage(albumErrors);

  const [isNestedModalOpen, setNestedModalOpen] = useState(false);

  const copyrightYearError = useMemo(
    () =>
      fieldHasErrors('CopyrightYear')
        ? findErrorMessage('CopyrightYear')
        : undefined,
    [albumErrors, findErrorMessage]
  );

  const copyrightLineError = useMemo(
    () =>
      fieldHasErrors('CopyrightLine')
        ? findErrorMessage('CopyrightLine')
        : undefined,
    [albumErrors, findErrorMessage]
  );
  const phonographicCopyrightYearError = useMemo(
    () =>
      fieldHasErrors('PhonographicCopyrightYear')
        ? findErrorMessage('PhonographicCopyrightYear')
        : undefined,
    [albumErrors, findErrorMessage]
  );
  const phonographicCopyrightLineError = useMemo(
    () =>
      fieldHasErrors('PhonographicCopyrightLine')
        ? findErrorMessage('PhonographicCopyrightLine')
        : undefined,
    [albumErrors, findErrorMessage]
  );

  useEffect(() => {
    const keyDownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isNestedModalOpen) return;
        onCancel();
      }
    };
    document.addEventListener('keydown', keyDownHandler);
    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [onCancel, isNestedModalOpen]);

  const formGroupProps: FormGroupProps = {
    readonly,
    album,
    showValidationErrors: showErrors,
    fieldHasErrors,
    fieldValidationState,
    findErrorMessage,
  };

  const isChangeRequest = !!changeRequest;

  return (
    <NestedModalContext.Provider
      value={{ isNestedModalOpen, setNestedModalOpen }}
    >
      <CancelChangesModal
        onSubmit={handleSubmit}
        onClose={closePopUp}
        onCancel={() => setShowCancelChangesModal(false)}
        show={showCancelChangesModal}
        titleText={t('product.editModeProduct.title')}
        bodyText={t('product.editModeProduct.cancelChangesBody')}
      />
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const submitter = (e.nativeEvent as SubmitEvent)
            .submitter as HTMLElement | null;
          if (submitter && !submitter.classList.contains('buttonSave')) {
            return; // Ignore submissions bubbled up from nested modal buttons
          }
          handleSubmit();
        }}
      >
        {showPopUp ? (
          <div
            data-testid="createProduct"
            id="productModal"
            className="product-edit-mode-modal"
          >
            <div className="product-edit-mode-modal-content">
              <div className="product-edit-mode-modal-header">
                <button
                  className="btn-close"
                  type="button"
                  aria-label="Close"
                  onClick={onCancel}
                >
                  <i className="icon fas fa-times fa-lg" aria-hidden="true"></i>
                </button>
                <h2>
                  {!readonly
                    ? t('product.editModeProduct.title')
                    : t('product.editModeProduct.productDetails')}
                </h2>
                <div className="edit-modal-spacer"></div>
              </div>

              <div
                className={`product-edit-mode-modal-body ${readonly ? 'readonly-modal' : ''}`}
              >
                <div className="scroll-body">
                  <DismissibleErrorAlert
                    error={saveErrorMessage}
                    onClose={() => setSaveErrorMessage(null)}
                  />
                  {!readonly ? (
                    <ValidationErrors
                      errorBanner={errorBanner}
                      errors={errorMessages}
                      validationErrors={albumIssues}
                      region={'Edit'}
                    ></ValidationErrors>
                  ) : null}
                  <div className="edit-form">
                    {/* Title */}
                    <ChangeRequestIndicator
                      show={currentChanges?.title?.hasChanges}
                      displayValue={currentChanges?.title?.value}
                    ></ChangeRequestIndicator>
                    <TitleFormGroup
                      {...formGroupProps}
                      labelTitle={t('product.editProduct.title')}
                      handleAlbumField={handleAlbumField}
                    />

                    {/* Version */}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentChanges?.versions?.hasChanges}
                        displayValue={currentChanges?.versions?.value}
                        customClass={`product-version-change-indicator ${album.albumMetaData?.versions?.length === 3 ? 'last-version' : ''}`}
                      ></ChangeRequestIndicator>
                      <ProductVersionsFormGroup
                        {...formGroupProps}
                        handleVersion={handleVersion}
                      />
                    </div>

                    {/* Release Type */}
                    {!isVideoProduct() && (
                      <>
                        <ChangeRequestIndicator
                          show={currentChanges?.releaseType?.hasChanges}
                          displayValue={currentChanges?.releaseType?.value}
                        ></ChangeRequestIndicator>
                        <ReleaseTypeFormGroup
                          {...formGroupProps}
                          releaseTypes={releaseTypes ?? []}
                          loadReleaseTypes={loadReleaseTypes}
                          handleReleaseType={handleReleaseType}
                        />
                      </>
                    )}

                    {/* Video Content Type */}
                    {isVideoProduct() && (
                      <>
                        <ChangeRequestIndicator
                          show={currentChanges?.videoContentType?.hasChanges}
                          displayValue={currentChanges?.videoContentType?.value}
                        ></ChangeRequestIndicator>
                        <VideoContentTypeFormGroup
                          {...formGroupProps}
                          handleVideoContentType={handleVideoContentType}
                        />
                      </>
                    )}

                    {/* Internal Description */}
                    <ChangeRequestIndicator
                      show={currentChanges?.internalDescription?.hasChanges}
                      displayValue={currentChanges?.internalDescription?.value}
                    ></ChangeRequestIndicator>
                    <InternalDescriptionFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Artist */}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentChanges?.artists?.hasChanges}
                        displayValue={currentChanges?.artists?.value}
                        customObjectKeys={['name', 'performerRole']}
                        customClass="product-artist-change-indicator"
                      ></ChangeRequestIndicator>
                      <ArtistFormGroup
                        {...formGroupProps}
                        errorBanner={errorBanner}
                        labelTitle={t('product.editProduct.artist')}
                        handleArtist={handleArtist}
                      />
                    </div>

                    {/* Metadata Language */}
                    <ChangeRequestIndicator
                      show={currentChanges?.metadataLanguage?.hasChanges}
                      displayValue={
                        metadataLanguages?.find(
                          (lang) =>
                            lang.value ===
                            currentChanges?.metadataLanguage?.value
                        )?.label
                      }
                    ></ChangeRequestIndicator>
                    <MetadataLanguageFormGroup
                      {...formGroupProps}
                      metadataLanguages={metadataLanguages ?? []}
                      loadMetadataLanguages={loadMetadataLanguages}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Labels */}
                    <LabelFormGroup
                      {...formGroupProps}
                      loadLabels={loadLabels}
                      handleLabelSelect={handleLabelSelect}
                      disabled={isChangeRequest}
                    />

                    {/* Gtin */}
                    <GtinFormGroup
                      {...formGroupProps}
                      handleAlbumField={handleAlbumField}
                      disabled={isChangeRequest}
                      displayValueOnly={isChangeRequest}
                    />

                    {/* Catalog Number */}
                    <ChangeRequestIndicator
                      show={currentChanges?.catalogNumber?.hasChanges}
                      displayValue={currentChanges?.catalogNumber?.value}
                    ></ChangeRequestIndicator>
                    <CatalogNumberFormGroup
                      {...formGroupProps}
                      errorBanner={errorBanner}
                      labelTitle={t('product.editProduct.catalogNumber')}
                      maxLength={15}
                      handleAlbumField={handleAlbumField}
                      disabled={isChangeRequest}
                    />

                    {/* Genre */}
                    <ChangeRequestIndicator
                      show={currentChanges?.genre?.hasChanges}
                      displayValue={currentChanges?.genre?.value}
                      customObjectKeys={['masterGenre', 'name']}
                      customSeparator={' > '}
                    ></ChangeRequestIndicator>
                    <GenreFormGroup
                      {...formGroupProps}
                      genres={genres ?? []}
                      loadGenres={loadGenres}
                      handleGenre={handleGenre}
                    />

                    {/* Manufactured by Ingrooves */}
                    <ChangeRequestIndicator
                      show={
                        currentChanges?.isManufacturedByIngrooves?.hasChanges
                      }
                      displayValue={
                        currentChanges?.isManufacturedByIngrooves?.value
                      }
                    ></ChangeRequestIndicator>
                    <ManufacturedByVmgFormGroup
                      {...formGroupProps}
                      handleRadioButton={handleRadioButton}
                    />

                    {/* Release Date */}
                    <ChangeRequestIndicator
                      show={currentChanges?.releaseDate?.hasChanges}
                      displayValue={currentChanges?.releaseDate?.value}
                    ></ChangeRequestIndicator>
                    <ReleaseDateFormGroup
                      {...formGroupProps}
                      albumValidationMetadata={albumValidationMetadata}
                      showValidationErrors={showValidationErrors}
                      albumErrors={albumErrors}
                      onChange={(releaseDate) =>
                        setAlbum({ ...album, releaseDate })
                      }
                    />

                    {/* Original Release Date */}
                    <ChangeRequestIndicator
                      show={currentChanges?.originalReleaseDate?.hasChanges}
                      displayValue={currentChanges?.originalReleaseDate?.value}
                    ></ChangeRequestIndicator>
                    <OriginalReleaseDateFormGroup
                      {...formGroupProps}
                      isOriginalReleaseDateInvalid={
                        isOriginalReleaseDateInvalid
                      }
                      getCurrentReleaseDate={getCurrentReleaseDate}
                      onOriginalReleaseDateChange={onOriginalReleaseDateChange}
                      originalReleaseDateOpen={originalReleaseDateOpen}
                      setOriginalReleaseDateOpen={setOriginalReleaseDateOpen}
                      useReleaseDateVisible={useReleaseDateVisible}
                      activateUseReleaseDate={activateUseReleaseDate}
                      setActivateUseReleaseDate={setActivateUseReleaseDate}
                      useReleaseDate={useReleaseDate}
                    />

                    {/* Exclusive Release Account */}
                    <ChangeRequestIndicator
                      show={currentChanges?.accountExclusive?.hasChanges}
                      displayValue={[
                        currentChanges?.accountExclusive?.value
                          ?.exclusiveReleaseAccount,
                        currentChanges?.accountExclusive?.value
                          ?.exclusiveReleaseAccountName,
                      ]}
                    ></ChangeRequestIndicator>
                    <AccountExclusiveReleaseFormGroup
                      {...formGroupProps}
                      albumErrors={albumErrors}
                      onChangeTargetAccount={onChangeTargetAccount}
                      onClickTargetAccount={onClickTargetAccount}
                      yesOption={yesOption}
                      noOption={noOption}
                      setOnClickTargetAccount={setOnClickTargetAccount}
                      setOnChangeTargetAccount={setOnChangeTargetAccount}
                      handleAccountExclusiveChange={
                        handleAccountExclusiveChange
                      }
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Preorder Date/DateTime */}
                    <ChangeRequestIndicator
                      show={currentChanges?.retailPreOrder?.hasChanges}
                      displayValue={currentChanges?.retailPreOrder?.value}
                    ></ChangeRequestIndicator>
                    <RetailPreOrderDateFormGroup
                      {...formGroupProps}
                      albumErrors={albumErrors}
                      onChangePreorderDate={onChangePreorderDate}
                      setOnChangePreorderDate={setOnChangePreorderDate}
                      handleAlbumMetaData={handleAlbumMetaData}
                      getPreviousReleaseDate={getPreviousReleaseDate}
                    />

                    {/* Copyright Year/Line */}
                    <ChangeRequestIndicator
                      show={
                        currentChanges?.copyrightYear?.hasChanges ||
                        currentChanges?.copyrightLine?.hasChanges
                      }
                      displayValue={[
                        currentChanges?.copyrightYear?.value,
                        currentChanges?.copyrightLine?.value,
                      ]}
                    ></ChangeRequestIndicator>
                    <CopyrightLineFormGroup
                      {...formGroupProps}
                      copyrightYearError={copyrightYearError}
                      copyrightLineError={copyrightLineError}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Phonographic Copyright Year/Line */}
                    <ChangeRequestIndicator
                      show={
                        currentChanges?.phonographicCopyrightYear?.hasChanges ||
                        currentChanges?.phonographicCopyrightLine?.hasChanges
                      }
                      displayValue={[
                        currentChanges?.phonographicCopyrightYear?.value,
                        currentChanges?.phonographicCopyrightLine?.value,
                      ]}
                    ></ChangeRequestIndicator>
                    <PhonographicCopyrightLineFormGroup
                      {...formGroupProps}
                      phonographicCopyrightYearError={
                        phonographicCopyrightYearError
                      }
                      phonographicCopyrightLineError={
                        phonographicCopyrightLineError
                      }
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Explicit Lyrics */}
                    <ChangeRequestIndicator
                      show={currentChanges?.explicitLyrics?.hasChanges}
                      displayValue={currentChanges?.explicitLyrics?.value}
                    ></ChangeRequestIndicator>
                    <ExplicitLyricsFormGroup
                      {...formGroupProps}
                      handleManufacturing={handleManufacturing}
                    />

                    {/* Packaging */}
                    <ChangeRequestIndicator
                      show={currentChanges?.packaging?.hasChanges}
                      displayValue={currentChanges?.packaging?.value?.name}
                    ></ChangeRequestIndicator>
                    <PackagingFormGroup
                      {...formGroupProps}
                      packagings={packagings ?? []}
                      loadPackaging={loadPackaging}
                      handlePackaging={handlePackaging}
                    />

                    {/* Packaging Description */}
                    <ChangeRequestIndicator
                      show={currentChanges?.packagingDescription?.hasChanges}
                      displayValue={currentChanges?.packagingDescription?.value}
                    ></ChangeRequestIndicator>
                    <PackagingDescriptionFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Ingrooves Canada Right */}
                    <ChangeRequestIndicator
                      show={currentChanges?.ingroovesCanadaRight?.hasChanges}
                      displayValue={currentChanges?.ingroovesCanadaRight?.value}
                    ></ChangeRequestIndicator>
                    <IngroovesCanadaRightsFormGroup
                      {...formGroupProps}
                      yesOption={yesOption}
                      noOption={noOption}
                      handleRadioButton={handleRadioButton}
                    />

                    {/* Country of Manufacture */}
                    <ChangeRequestIndicator
                      show={currentChanges?.countryOfManufacture?.hasChanges}
                      displayValue={
                        territories?.find(
                          (t) =>
                            t.value ===
                            currentChanges?.countryOfManufacture?.value
                        )?.label
                      }
                    ></ChangeRequestIndicator>
                    <CountryOfManufactureFormGroup
                      {...formGroupProps}
                      territories={territories ?? []}
                      loadTerritories={loadTerritories}
                      handleManufacturing={handleManufacturing}
                    />

                    {/* Currency Manufacturing Cost Per Unit */}
                    <ChangeRequestIndicator
                      show={
                        currentChanges?.currencyManufacturingCostPerUnit
                          ?.hasChanges
                      }
                      displayValue={
                        currentChanges?.currencyManufacturingCostPerUnit?.value
                      }
                      customObjectKeys={[
                        'currency',
                        'manufacturingCostPerUnit',
                      ]}
                      customSeparator={' '}
                    ></ChangeRequestIndicator>
                    <ManufacturingCostFormGroup
                      {...formGroupProps}
                      handleManufacturing={handleManufacturing}
                      handleManufacturingCostBlur={handleManufacturingCostBlur}
                    />

                    {/* Units Per Sets */}
                    <ChangeRequestIndicator
                      show={currentChanges?.unitsPerSets?.hasChanges}
                      displayValue={currentChanges?.unitsPerSets?.value}
                    ></ChangeRequestIndicator>
                    <UnitsPerSetFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Box Count */}
                    <ChangeRequestIndicator
                      show={currentChanges?.boxCount?.hasChanges}
                      displayValue={currentChanges?.boxCount?.value}
                    ></ChangeRequestIndicator>
                    <BoxCountFormGroup
                      {...formGroupProps}
                      handleAlbumMetaData={handleAlbumMetaData}
                    />

                    {/* Projected Physical IO */}
                    <ChangeRequestIndicator
                      show={currentChanges?.projectedPhysicalIO?.hasChanges}
                      displayValue={currentChanges?.projectedPhysicalIO?.value}
                    ></ChangeRequestIndicator>
                    <ProjectedPhysicalIOFormGroup
                      {...formGroupProps}
                      handleSaleProjection={handleSaleProjection}
                    />

                    {/* Project Overview */}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentChanges?.projectOverview?.hasChanges}
                        displayValue={currentChanges?.projectOverview?.value}
                        customClass="product-project-overview-change-indicator"
                      ></ChangeRequestIndicator>
                      <ProjectOverviewFormGroup
                        {...formGroupProps}
                        handleMarketingInfo={handleMarketingInfo}
                      />
                    </div>

                    {/* Marketing Information */}
                    <div className="change-indicator-field">
                      <ChangeRequestIndicator
                        show={currentChanges?.marketingInformation?.hasChanges}
                        displayValue={
                          currentChanges?.marketingInformation?.value
                        }
                        customClass="product-marketing-change-indicator"
                      ></ChangeRequestIndicator>
                      <MarketingInformationFormGroup
                        {...formGroupProps}
                        handleMarketingInfo={handleMarketingInfo}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="edit-modal-spacer"></div>
                <div className="modal-footer-buttons">
                  {!readonly ? (
                    <>
                      <button
                        type="button"
                        className="btn btn-primary alt"
                        onClick={onCancel}
                        disabled={isSubmitting || disabled}
                      >
                        {t('common.button.cancel')}
                      </button>
                      <button
                        type="submit"
                        data-testid="Save"
                        className="btn btn-primary buttonSave"
                        disabled={isSaveDisabled || isSubmitting || disabled}
                      >
                        {t('common.button.save')}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closePopUp}
                    >
                      {t('common.button.close')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </form>
    </NestedModalContext.Provider>
  );
};

export default ProductDetailsEdit;
